import { CURRENCY_CODE, formatToInternational } from '@shared/utils'
import type { InfoSheetContent } from '@uiKit/components/InfoSheet.vue'
import { LISTING_VALIDITY_DAYS, formatMarketTokenPrice, type TradeFeeLine } from '../../trading'

// Copy for the sell drawer (Figma 34796:138630 → 34796:138922) and the ⓘ
// sheets behind it (Permitted band 34842:195032, Gross sale value 34911:85120,
// Performance fee 34842:195046, Platform fee 34842:195039, VAT 34911:85128).
// Rates and amounts are the server's (sell-overview, sell-quote) — copy only
// frames them.

export type SellStep =
  | 'overview'
  | 'dividend'
  | 'quantity'
  | 'price'
  | 'review'
  | 'underReview'
  | 'rejected'

export const SELL_STEP_TITLES: Record<SellStep, string> = {
  overview: 'Sell Tokens',
  dividend: 'Sell Tokens',
  quantity: 'Sell Tokens',
  price: 'Sell Tokens',
  review: 'Confirm Listing',
  underReview: 'Confirm Listing',
  rejected: 'Confirm Listing',
}

export const SELL_STEPPER_LABELS = ['Exit Overview', 'Token Details', 'Amount Details'] as const

// ── Exit overview + dividend notice (spec 1.7.3) ─────────────────────────

export const EXIT_OVERVIEW_NOTE =
  "These figures use the latest independent valuation. Your listing price is fixed once it's live — to change it, cancel and relist."

export const DIVIDEND_NOTICE = {
  title: 'Before you list: rental income timing',
  text: 'Rental entitlement follows the holder at 00:00 on the 1st of the month. If your tokens sell before then, the buyer — not you — receives that month’s rental income.',
  acknowledgement: 'I understand how rental income is assigned when my tokens sell.',
}

export const DIVIDEND_NOTICE_REQUIRED =
  'Confirm you have read the rental income notice to continue.'

export const NOT_TRADABLE_NOTE = {
  title: 'Not available on the Marketplace',
  text: "This property isn't available on the Marketplace, so its tokens can't be listed.",
}

export const CANNOT_SELL_TITLE = "You can't list these tokens yet"

// ── Quantity / price ─────────────────────────────────────────────────────

export const LOCKED_TOKENS_NOTE = {
  title: 'Your tokens will be locked',
  text: "Listed tokens are locked while the listing is open — they can't be sold, transferred or listed again until it closes.",
}

export const outsideBandNote = (bandPct: number) => ({
  title: 'Outside the permitted band',
  text: `A listing price must stay within ±${bandPct}% of the property's latest quarterly valuation.`,
})

export const BAND_INSIDE_LABEL = 'Your price sits inside the permitted band.'
export const BAND_OUTSIDE_LABEL = 'Your price is outside the permitted band.'

export const KEY_RISKS = [
  'There is no guarantee your tokens will sell.',
  "A listing can't be edited — to change price or quantity you will have to cancel and relist.",
  `Your tokens stay locked for the ${LISTING_VALIDITY_DAYS} days the listing runs.`,
  'Settlement timing depends on DLD registration and approval.',
]

/** Terms tick copy, split so the middle reads as a link (Figma 34842:195022). */
export const SELL_TERMS_LABEL = {
  before: 'I accept the ',
  link: 'Secondary Sale Terms & Conditions',
  after: ' and the risks above.',
}
export const SELL_TERMS_REQUIRED = 'Accept the terms to create your listing.'
export const CREATE_LISTING_FAILED = "We couldn't create your listing. Please try again."
export const SELL_QUOTE_PENDING = 'Pricing your listing…'

export const UNDER_REVIEW_NOTE = `If the valuation check passes, your listing goes live and the ${LISTING_VALIDITY_DAYS}-day clock starts. If it fails, your tokens are unlocked and we'll tell you why.`
export const DEFAULT_REJECTION_REASON =
  'The price did not pass the valuation check against the latest quarterly valuation.'
export const REJECTED_NOTE =
  'Nothing was charged. You can create a new listing at a compliant price whenever you like.'

export const underReviewSubtitle = (tokens: number): string =>
  `Your ${formatToInternational(tokens)} tokens are now locked. We'll notify you when the valuation check completes.`

export const rejectedSubtitle = (tokens: number): string =>
  `Your ${formatToInternational(tokens)} tokens have been unlocked and returned to your holding.`

// ── ⓘ sheets ────────────────────────────────────────────────────────────

/** "AED 0.86 – 1.16" — the band with the currency written once. */
const formatBandShort = (min: number, max: number): string =>
  `${formatMarketTokenPrice(min)} – ${formatMarketTokenPrice(max).replace(`${CURRENCY_CODE} `, '')}`

interface PermittedBandSheetInput {
  valuation: number
  bandMin: number
  bandMax: number
  bandPct: number
}

export const buildPermittedBandSheet = ({
  valuation,
  bandMin,
  bandMax,
  bandPct,
}: PermittedBandSheetInput): InfoSheetContent => ({
  title: 'Permitted band',
  note: 'The price range within which you can list your tokens',
  description: `You can list your price within ${bandPct}% of the latest DLD valuation, so prices on the marketplace stay tied to what the property is actually worth. A price outside the band is blocked.`,
  gutter: 'auto',
  rows: [
    { label: 'Latest DLD valuation', value: formatMarketTokenPrice(valuation) },
    { label: `Lowest you can ask, ${bandPct}% under`, value: formatMarketTokenPrice(bandMin) },
    { label: `Highest you can ask, ${bandPct}% over`, value: formatMarketTokenPrice(bandMax) },
    {
      operator: '=',
      label: 'Permitted band',
      value: formatBandShort(bandMin, bandMax),
      isResult: true,
    },
  ],
})

export const GROSS_SALE_VALUE_SHEET: InfoSheetContent = {
  title: 'Gross sale value',
  note: 'This is the total value of your listed tokens before any fees are deducted from it',
  description:
    'The number of tokens you are selling multiplied by the price you set. Your fees are worked out from this figure, and what is left over is paid into your wallet.',
}

/** Fee codes the sell quote itemises (performance fee is on the gain only). */
export const PERFORMANCE_FEE_CODE = 'performance_fee'

const rateText = (fee: TradeFeeLine): string => (fee.rate ? ` (${fee.rate})` : '')

/** ⓘ for one sell-side fee line, worded per fee. */
export const buildSellFeeSheet = (fee: TradeFeeLine, averageCost: number): InfoSheetContent =>
  fee.code.toLowerCase() === PERFORMANCE_FEE_CODE
    ? {
        title: `${fee.label}${rateText(fee)}`,
        note: 'Fee charged on your gain only — never on what you originally paid.',
        description: `When your tokens sell, Sliceal takes a share of the difference between the sale price and your average cost of ${formatMarketTokenPrice(averageCost)} per token. If you sell at or below your cost, there is no performance fee.`,
      }
    : {
        title: `${fee.label}${rateText(fee)}`,
        note: 'Sliceal’s fee for matching and settling your sale',
        description: 'Charged once, when your tokens sell, on the gross sale value.',
      }

export const buildSellFeeVatSheet = (fee: TradeFeeLine): InfoSheetContent => ({
  title: `VAT on ${fee.label}`,
  note: `UAE VAT, charged on the ${fee.label} only`,
  description:
    'There’s no VAT on your sale amount or on government charges. You’ll find it itemised on your tax invoice.',
})
